import React from 'react'
import Link from 'gatsby-link'
import Img from 'gatsby-image'
import Helmet from 'react-helmet'
import styled from 'styled-components'
import Gallery from '../components/gallery'
import i18n from '../components/i18n'

const ContentWrapper = styled.div`
  max-width: 60rem;
  margin-left: auto;
  margin-right: auto;
  margin-bottom: 10vh;
  padding: 3rem;
  font-size: 1.25rem;
  line-height: 1.5;

  @media (min-width: 52em) {
  }
`

const PageTitle = styled.div`
  max-width: 48em;
  padding: 1rem;
  margin-left: auto;
  margin-right: auto;
`

const Title = styled.h1`
  position: relative;
  margin-top: 20vh;
  margin-bottom: 4rem;
  text-align: center;

  span {
    display: block;
    font-size: 2rem;
    margin-top: 1rem;
    padding-top: 1rem;

    &:before {
      position: absolute;
      left: 50%;
      content: "";
      width: 4rem;
      height: .25rem;
      margin-top: -1rem;
      margin-left: -2rem;
      background-color: rgb(0,186,242);
    }
  }

  @media (min-width: 48em) {
    span {
      font-size: 3rem;
    }
  }
`

const PageHero = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  max-width: 64em;
  margin: 3rem auto;
  padding: 1rem;

  @media (min-width: 40em) {
    padding: 2rem;
  }
`

const Body = styled.div`
  max-width: 52em;
  margin-left: auto;
  margin-right: auto;
  padding-left: 1rem;
  padding-right: 1rem;

  @media (min-width: 40em) {
    padding-left: 2rem;
    padding-right: 2rem;
  }
`

const BodyText = styled.div`
  @media (min-width: 52em) {
    font-size: 1.25rem;
  }
`

const PageGallery = styled.div`
  padding: 1rem 0;

  @media (min-width: 40em) {
    padding: 2rem 0;
  }

  @media (min-width: 52em) {
    padding: 3rem 0;
  }
`

const PageHeroImage = styled.div`
  width: 100%;

  @media (min-width: 52em) {
    width: 50%;
  }
`

const PageHeroCaption = styled.div`
  width: 100%;
  margin-top: 1rem;
  color: #606060;
  font-size: .875rem;

  @media (min-width: 52em) {
    width: 50%;
    margin-top: 0;
    padding-left: 2rem;
    padding-right: 2rem;
    font-size: 1rem;
  }
`

const PageNavigation = styled.div`
  border-top: 1px solid #ddd;
  border-bottom: 1px solid #ddd;
  background-color: #fff;

  @media (min-width: 40em) {
    display: flex;
  }
`

const NavItem = styled.div`
  width: 100%;
  text-align: center;

  @media (min-width: 40em) {
    display: flex;
    flex-direction: column;
    justify-content: center;
    width: 50%;
    padding: 2rem;
    border-left: 1px solid #ddd;
  }
`

const NavLink = styled(Link)`
  color: #303030;
  text-decoration: none;

  p {
    margin: 0;
  }
`

const NavImg = styled(Img)`
  max-width: 10em;
  margin-left: auto;
  margin-right: auto;
`

const NavHeading = styled.h4`
  font-size: 1rem;
  margin-bottom: 0;
  margin-top: 1rem;

  @media (min-width: 52em) {
    font-size: 1.25rem;
  }

  @media (min-width: 64em) {
    font-size: 1.5rem;
  }
`

export default ({ data }) => {
  const page = data.contentfulPage
  const pages = data.pages.edges.filter(item => item.node.node_locale === page.node_locale)
  const pageTitle = page.title + " - " + page.subtitle

  if (i18n.language != page.node_locale)
    i18n.changeLanguage(page.node_locale)

  let prev, next = null

  pages.forEach((item, index) => {
    if (item.node.id == page.id) {
      if (index > 0)
        prev = index - 1
      if (index < pages.length - 1)
        next = index + 1
    }
  })

  return (
    <div>
      <Helmet
        title={pageTitle}
        meta={[
          { name: 'description', content: 'Itsenäisen Suomen poliisi' },
          { name: 'keywords', content: 'poliisimuseo, poliisi, verkkonäyttely, itsenäisen suomen poliisi' },
        ]}
      />
      <PageTitle>
        <Title>
          <small>{page.title}</small>
          <span>{page.subtitle}</span>
        </Title>
      </PageTitle>

      <PageHero>
        <PageHeroImage>
          <Img sizes={page.image.sizes} />
        </PageHeroImage>
        <PageHeroCaption>
          <div dangerouslySetInnerHTML={{ __html: page.imageCaption.childMarkdownRemark.html }} />
        </PageHeroCaption>
      </PageHero>

      <Body>
        <BodyText dangerouslySetInnerHTML={{ __html: page.body.childMarkdownRemark.html }} />
      </Body>

      <PageGallery>
        <Gallery images={page.imageGallery} />
      </PageGallery>

      <PageNavigation>
        {prev &&
          <NavItem>
            <NavLink to={pages[prev].node.slug}>
              <NavImg sizes={pages[prev].node.image.sizes}/>
              <NavHeading>{pages[prev].node.title}</NavHeading>
              <p>{pages[prev].node.subtitle}</p>
            </NavLink>
          </NavItem>
        }
        {next &&
          <NavItem>
            <NavLink to={pages[next].node.slug}>
              <NavImg sizes={pages[next].node.image.sizes}/>
              <NavHeading>{pages[next].node.title}</NavHeading>
              <p>{pages[next].node.subtitle}</p>
            </NavLink>
          </NavItem>
        }
      </PageNavigation>
    </div>
  )
}

export const pageQuery = graphql`
  query pageQuery($id: String!) {
  	contentfulPage(id: { eq: $id }) {
      id
      node_locale
      title
      slug
      subtitle
      image {
        sizes(maxWidth: 960) {
          base64
          aspectRatio
          src
          srcSet
          sizes
        }
      }
      imageCaption {
        childMarkdownRemark {
          html
        }
      }
      body {
        childMarkdownRemark {
          html
        }
      }
      imageGallery {
        image {
          sizes(maxWidth: 960) {
            base64
            aspectRatio
            src
            srcSet
            sizes
          }
        }
        caption {
          childMarkdownRemark {
            html
          }
        }
      }
  	}
    pages: allContentfulPage(
      filter: { featuredContent: { eq: null } }
      sort: { fields: [title] }
    ) {
      edges {
        node {
          id
          node_locale
          title
          slug
          subtitle
          image {
            sizes {
              base64
              aspectRatio
              src
              srcSet
              sizes
            }
          }
        }
      }
    }

  }
`



// WEBPACK FOOTER //
// ./src/templates/page.js